/**
 * Sound, synthesised.
 *
 * Short WebAudio tones rather than files: nothing to download, nothing to cache,
 * and the pitch can follow the combo so a streak literally rises. Off by
 * default — thirty tablets chirping at once is not something to inflict on a
 * classroom without the teacher asking for it.
 *
 * The one thing that is not a tone is applause, which is filtered noise. It
 * still ships as code rather than a recording, so the encouragement works on a
 * fresh install with nothing uploaded — and a teacher who records «أحسنت» in
 * their own voice replaces it with `setPraiseClips`.
 */

let ctx: AudioContext | null = null;
let enabled = false;

export function setSoundEnabled(on: boolean): void {
  enabled = on;
  if (on) void resume();
}

export const soundEnabled = (): boolean => enabled;

async function resume(): Promise<void> {
  ctx ??= new AudioContext();
  if (ctx.state === 'suspended') await ctx.resume();
}

interface Note { freq: number; at: number; ms: number; gain?: number; type?: OscillatorType }

function audio(): AudioContext | null {
  if (!enabled) return null;
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function play(notes: Note[]): void {
  const c = audio();
  if (!c) return;
  try {
    const now = c.currentTime;
    for (const n of notes) {
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = n.type ?? 'triangle';
      osc.frequency.value = n.freq;
      const start = now + n.at / 1000;
      const end = start + n.ms / 1000;
      // A short attack and an exponential tail: a square envelope clicks.
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(n.gain ?? 0.12, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      osc.connect(gain).connect(c.destination);
      osc.start(start);
      osc.stop(end + 0.02);
    }
  } catch {
    /* no audio device, or autoplay still blocked — silence is an acceptable failure */
  }
}

/** Rising with the combo, capped so a long streak does not become a whistle. */
export function playMatch(combo: number): void {
  const step = Math.min(Math.max(combo - 1, 0), 12);
  const base = 523.25 * 2 ** (step / 12);
  play([
    { freq: base, at: 0, ms: 90 },
    { freq: base * 1.5, at: 55, ms: 110, gain: 0.09 },
  ]);
}

export function playMiss(): void {
  play([
    { freq: 196, at: 0, ms: 130, type: 'sine', gain: 0.1 },
    { freq: 155, at: 70, ms: 150, type: 'sine', gain: 0.08 },
  ]);
}

/** A four-note fanfare: the badge should sound like an event, not a click. */
export function playBadge(): void {
  const notes = [523.25, 659.25, 783.99, 1046.5];
  play(notes.map((freq, i) => ({ freq, at: i * 95, ms: 240, gain: 0.11 })));
}

export function playClear(): void {
  const notes = [392, 523.25, 659.25];
  play(notes.map((freq, i) => ({ freq, at: i * 70, ms: 300, gain: 0.1 })));
  playApplause(1800);
}

/* ── applause ──────────────────────────────────────────────────────────────
 * One clap is a very short burst of noise pushed through a band-pass filter;
 * scatter forty of them across a second and a half on a decaying density curve
 * and it is a small room applauding. Randomised centre frequency per clap is
 * what stops it sounding like one clap repeated.
 * ────────────────────────────────────────────────────────────────────────── */

function clap(c: AudioContext, at: number, gain: number): void {
  const len = Math.floor(0.055 * c.sampleRate);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2.6;
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 1100 + Math.random() * 1400;
  bp.Q.value = 0.7 + Math.random() * 0.6;
  const g = c.createGain();
  g.gain.value = gain;
  src.connect(bp).connect(g).connect(c.destination);
  src.start(c.currentTime + at / 1000);
}

export function playApplause(ms = 1400): void {
  const c = audio();
  if (!c) return;
  try {
    for (let i = 0; i < 44; i++) {
      // Front-loaded: a room starts clapping together and trails off.
      const t = Math.random() ** 0.65 * ms;
      clap(c, t, 0.05 + Math.random() * 0.09 * (1 - t / ms));
    }
  } catch {
    /* as above */
  }
}

/* ── the word of praise ────────────────────────────────────────────────────
 * Default is a bright two-note flourish over the match tone. A teacher who has
 * uploaded recordings gets those instead, one at random, never the same twice
 * running — «أحسنت» on a loop stops being encouragement by the third board.
 * ────────────────────────────────────────────────────────────────────────── */

let clips: string[] = [];
let lastClip = -1;
const cache = new Map<string, HTMLAudioElement>();

export function setPraiseClips(ids: readonly string[]): void {
  clips = [...ids];
  lastClip = -1;
  for (const id of clips) {
    if (cache.has(id)) continue;
    const el = new Audio(`/api/assets/${id}/raw`);
    el.preload = 'auto';
    cache.set(id, el);
  }
}

export const hasPraiseClips = (): boolean => clips.length > 0;

export function playPraise(): void {
  if (!enabled) return;
  if (clips.length === 0) {
    play([
      { freq: 783.99, at: 0, ms: 130, gain: 0.1 },
      { freq: 1046.5, at: 90, ms: 200, gain: 0.09 },
    ]);
    return;
  }
  let i = Math.floor(Math.random() * clips.length);
  if (clips.length > 1 && i === lastClip) i = (i + 1) % clips.length;
  lastClip = i;
  const el = cache.get(clips[i]!);
  if (!el) return;
  try {
    el.currentTime = 0;
    // A clip that will not play (autoplay policy, missing file) must not take
    // the rest of the celebration down with it.
    void el.play().catch(() => undefined);
  } catch {
    /* ignore */
  }
}
